'use client';

import React from 'react';
import {
  Receipt,
  ClipboardCheck,
  MessageSquare,
  MessageCircle,
  Shield,
  Check,
  ArrowRight,
} from 'lucide-react';
import Link from 'next/link';
import HomeFooter from './HomeFooter';

interface ParentAppSectionProps {
  guardianLoginUrl: string;
}

export function ParentAppSection({ guardianLoginUrl }: ParentAppSectionProps) {
  const sections = [
    {
      id: 'fees-receipts',
      icon: Receipt,
      title: 'Fees and receipts that stay organized',
      description: 'View current dues and upcoming fee cycles. Pay via QR code or reference transfer and upload proof instantly. Download receipts and fee statements any time.',
      highlights: [
        'View current dues and upcoming fee cycles',
        'Pay via QR code or reference transfer and upload proof instantly',
        'Download receipts and fee statements any time',
      ],
      graphic: 'fees',
    },
    {
      id: 'attendance-academics',
      icon: ClipboardCheck,
      title: 'Attendance and academics at a glance',
      description: 'Daily attendance summary with history. Exam schedules, admit cards, and results (as they go live). Event and holiday calendars synced with the school.',
      highlights: [
        'Daily attendance summary with history',
        'Exam schedules, admit cards, and results (as they go live)',
        'Event and holiday calendars synced with the school',
      ],
      graphic: 'attendance',
    },
    {
      id: 'conversations',
      icon: MessageSquare,
      title: 'Conversations that feel natural',
      description: 'Submit leave requests, edit until approved, and see status updates. Send feedback or questions straight to designated school teams. Receive notices, announcements, and attachments with read-anywhere access.',
      highlights: [
        'Submit leave requests, edit until approved, and see status updates',
        'Send feedback or questions straight to designated school teams',
        'Receive notices, announcements, and attachments with read-anywhere access',
      ],
      graphic: 'conversations',
    },
    {
      id: 'whatsapp-app',
      icon: MessageCircle,
      title: 'WhatsApp + App, together',
      description: 'Parents receive automated nudges for pending fees and acknowledgements. Payment proofs can be shared via WhatsApp bot and confirmed in-app. Multilingual templates and regional language support (roadmap) keep communication inclusive.',
      highlights: [
        'Parents receive automated nudges for pending fees and acknowledgements',
        'Payment proofs can be shared via WhatsApp bot and confirmed in-app',
        'Multilingual templates and regional language support (roadmap) keep communication inclusive',
      ],
      graphic: 'whatsapp',
    },
    {
      id: 'trust-privacy',
      icon: Shield,
      title: 'Trust and privacy',
      description: 'Parents see only their authorised wards and relevant documents. Data encrypted in transit; role-based access aligns with school policy. Clear opt-in settings for notifications and WhatsApp messaging.',
      highlights: [
        'Parents see only their authorised wards and relevant documents',
        'Data encrypted in transit; role-based access aligns with school policy',
        'Clear opt-in settings for notifications and WhatsApp messaging',
      ],
      graphic: 'privacy',
    },
  ];

  const renderGraphic = (type: string) => {
    switch (type) {
      case 'fees':
        return (
          <div className="w-full h-full bg-[#F5F8F9] rounded-lg flex items-center justify-center p-8">
            <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-square bg-white rounded-lg shadow-sm flex items-center justify-center">
                  <Receipt className="h-8 w-8 text-[#0D373D]" />
                </div>
              ))}
            </div>
          </div>
        );
      case 'attendance':
        return (
          <div className="w-full h-full bg-[#F5F8F9] rounded-lg flex items-center justify-center p-8">
            <div className="relative w-full max-w-sm">
              <div className="absolute inset-0 flex items-center justify-center">
                <ClipboardCheck className="h-24 w-24 text-[#0D373D]" />
              </div>
              <div className="relative grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="aspect-square bg-white rounded-lg shadow-sm" />
                ))}
              </div>
            </div>
          </div>
        );
      case 'conversations':
        return (
          <div className="w-full h-full bg-[#F5F8F9] rounded-lg flex items-center justify-center p-8">
            <div className="space-y-4 w-full max-w-xs">
              {[
                { icon: MessageSquare, label: 'Leave Requests' },
                { icon: MessageCircle, label: 'Feedback' },
                { icon: ClipboardCheck, label: 'Notices' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
                  <div className="w-10 h-10 bg-[#E2FDCB] rounded-lg flex items-center justify-center">
                    <item.icon className="h-5 w-5 text-[#0D373D]" />
                  </div>
                  <span className="text-sm font-medium text-[#1D2125]">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      case 'whatsapp':
        return (
          <div className="w-full h-full bg-[#F5F8F9] rounded-lg flex items-center justify-center p-8">
            <div className="relative w-full max-w-sm">
              <div className="absolute inset-0 flex items-center justify-center">
                <MessageCircle className="h-20 w-20 text-[#0D373D]" />
              </div>
              <div className="relative grid grid-cols-2 gap-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="aspect-square bg-white rounded-lg shadow-sm" />
                ))}
              </div>
            </div>
          </div>
        );
      case 'privacy':
        return (
          <div className="w-full h-full bg-[#F5F8F9] rounded-lg flex items-center justify-center p-8">
            <div className="grid grid-cols-2 gap-4 w-full max-w-xs">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-square bg-white rounded-lg shadow-sm flex items-center justify-center">
                  <Shield className="h-6 w-6 text-[#0D373D]" />
                </div>
              ))}
            </div>
          </div>
        );
      default:
        return (
          <div className="w-full h-full bg-[#F5F8F9] rounded-lg flex items-center justify-center">
            <div className="text-[#4D4F52]">Visual</div>
          </div>
        );
    }
  };

  return (
    <div className="space-y-12 xs:space-y-16 sm:space-y-20 md:space-y-24 lg:space-y-28">
      {/* Hero Section */}
      <div className="bg-[#F5F8F9] rounded-xl text-center space-y-6 sm:space-y-8 lg:space-y-10 px-3 xs:px-4 sm:px-6 md:px-8 pt-12 xs:pt-16 sm:pt-20 md:pt-24 lg:pt-28 xl:pt-32 pb-10 xs:pb-12 sm:pb-16 md:pb-20 lg:pb-24 xl:pb-28 mt-6 xs:mt-7 sm:mt-8 mx-2 sm:mx-4 lg:mx-8">
        <div className="space-y-4 sm:space-y-6 lg:space-y-8">
          <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight">
            <span className="block text-[#0D373D]">Parents in the loop</span>
            <span className="block mt-1 xs:mt-1.5 sm:mt-2 md:mt-3 text-[#1D2125]">
              without chasing{' '}
              <span className="inline-block px-2 xs:px-2.5 sm:px-3 py-0.5 xs:py-1 bg-[#E2FDCB] text-[#0D373D] rounded text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold">the school</span>
            </span>
          </h1>
          <p className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl text-[#4D4F52] max-w-xs xs:max-w-sm sm:max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto leading-relaxed px-2 xs:px-4 sm:px-0">
            The Nest Parent App keeps families informed about fees, attendance, events, and notices—backed by WhatsApp workflows for quick updates.
          </p>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center pt-4 sm:pt-6 lg:pt-8 px-2 xs:px-4 sm:px-0">
          <Link
            href={guardianLoginUrl}
            className="w-full xs:w-full sm:w-auto min-w-[160px] xs:min-w-[180px] flex items-center justify-center px-3 xs:px-4 py-2 xs:py-2.5 bg-[#0D373D] hover:bg-[#0A2A2F] text-white text-sm xs:text-base font-semibold rounded-xl transition-colors"
          >
            Explore the Parent App
            <ArrowRight className="ml-1.5 xs:ml-2 h-3 xs:h-4 w-3 xs:w-4" />
          </Link>
        </div>
      </div>

      {/* Main Content - Alternating Layout */}
      <div className="px-3 xs:px-4 sm:px-6 md:px-8 py-8 xs:py-10 sm:py-12 md:py-16 lg:py-20 bg-white mx-2 sm:mx-4 lg:mx-8">
        <div className="max-w-7xl mx-auto space-y-16 xs:space-y-18 sm:space-y-20 md:space-y-24 lg:space-y-32">
          {sections.map((section, index) => {
            const IconComponent = section.icon;
            const isEven = index % 2 === 0;
            
            return (
              <section
                key={section.id}
                className={`flex flex-col ${
                  isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'
                } items-center gap-6 xs:gap-7 sm:gap-8 lg:gap-16`}
              >
                {/* Content Side */}
                <div className="flex-1 w-full lg:w-auto">
                  <div className="mb-4 xs:mb-5 sm:mb-6">
                    <div className="flex items-center gap-3 xs:gap-4 mb-3 xs:mb-4">
                      <div className="w-12 xs:w-13 sm:w-14 h-12 xs:h-13 sm:h-14 bg-[#E2FDCB] rounded-lg flex items-center justify-center flex-shrink-0">
                        <IconComponent className="h-6 xs:h-6.5 sm:h-7 w-6 xs:w-6.5 sm:w-7 text-[#0D373D]" />
                      </div>
                      <h2 className="text-2xl xs:text-3xl sm:text-4xl font-bold text-[#1D2125]">
                        {section.title}
                      </h2>
                    </div>
                    <p className="text-sm xs:text-base sm:text-lg text-[#4D4F52] leading-relaxed mb-4 xs:mb-5 sm:mb-6">
                      {section.description}
                    </p>
                    {section.highlights.length > 0 && (
                      <div className="space-y-2 xs:space-y-3">
                        <ul className="space-y-1.5 xs:space-y-2">
                          {section.highlights.map((highlight, idx) => (
                            <li key={idx} className="flex items-start gap-2 xs:gap-3">
                              <Check className="h-4 xs:h-4.5 sm:h-5 w-4 xs:w-4.5 sm:w-5 text-[#22C55E] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                              <p className="text-xs xs:text-sm sm:text-base text-[#4D4F52] leading-relaxed">{highlight}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>

                {/* Graphic Side */}
                <div className="flex-1 w-full lg:w-auto">
                  <div className="w-full h-[250px] xs:h-[280px] sm:h-[300px] md:h-[400px] lg:h-[450px]">
                    {renderGraphic(section.graphic)}
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>

      {/* Adoption Tips Section */}
      <div className="px-3 xs:px-4 sm:px-6 md:px-8 py-8 xs:py-10 sm:py-12 md:py-16 lg:py-20 bg-[#F5F8F9] mx-2 sm:mx-4 lg:mx-8 rounded-xl">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8 xs:mb-10 sm:mb-12">
            <h2 className="text-2xl xs:text-3xl sm:text-4xl font-bold text-[#1D2125] mb-3 xs:mb-4">
              Adoption tips for schools
            </h2>
            <p className="text-sm xs:text-base sm:text-lg text-[#4D4F52] px-2 xs:px-4 sm:px-0">
              Everything you need to help parents get started with the Nest Parent App
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 xs:gap-5 sm:gap-6">
            {[
              'Launch campaign toolkit: posters, launch email/SMS copy, parent meeting deck',
              'Step-by-step onboarding checklist for staff and class teachers',
              'Parent FAQ handout covering payments, receipts, and support',
            ].map((tip, idx) => (
              <div key={idx} className="bg-white rounded-lg p-4 xs:p-5 sm:p-6 shadow-sm">
                <div className="flex items-start gap-2 xs:gap-3">
                  <Check className="h-4 xs:h-4.5 sm:h-5 w-4 xs:w-4.5 sm:w-5 text-[#22C55E] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                  <p className="text-sm xs:text-base text-[#4D4F52] leading-relaxed">{tip}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="px-3 xs:px-4 sm:px-6 md:px-8 py-8 xs:py-10 sm:py-12 md:py-16 lg:py-20 bg-white mx-2 sm:mx-4 lg:mx-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl xs:text-3xl sm:text-4xl font-bold text-[#1D2125] mb-3 xs:mb-4">
            Show parents the Nest experience.
          </h2>
          <p className="text-sm xs:text-base sm:text-lg text-[#4D4F52] leading-relaxed mb-6 xs:mb-7 sm:mb-8 px-2 xs:px-4 sm:px-0">
            Help families stay informed without extra calls or spreadsheets.
          </p>
          <Link
            href={guardianLoginUrl}
            className="inline-flex items-center justify-center px-5 xs:px-6 py-2 xs:py-2.5 bg-[#0D373D] text-white text-sm xs:text-base font-semibold rounded-xl hover:bg-[#0A2A2F] transition-colors duration-300"
          >
            Explore the Parent App
            <ArrowRight className="ml-1.5 xs:ml-2 h-3.5 xs:h-4 w-3.5 xs:w-4" />
          </Link>
        </div>
      </div>

      <div className="bg-[#F5F8F9] mx-2 sm:mx-4 lg:mx-8 rounded-xl">
        <div className="border-t border-[#E5E7EB]"></div>
        <HomeFooter />
      </div>

    </div>
  );
}

